/**
 * WonProjectSetup - the "you just won it, now what's the PO?" form.
 *
 * Opens straight after an opportunity is marked Won so nobody has to go and
 * find the row again on the Projects tab. The status is ALREADY saved by the
 * time this appears: it is deliberately skippable, because someone who does
 * not have the PO number yet must still be able to mark the job won.
 *
 * Every route to Won opens it (Paul Q-02): the quick Won button on the
 * Opportunities tab and on an account's Opportunities tab
 * (useCloseOpportunity), the editor's Status dropdown (EditOpportunityModal)
 * and "close as Won" in the next-step prompt (useCompleteTask), on the GLOBAL
 * KSA tab too.
 *
 * onSave(patch) may resolve to false: the form then stays open with the
 * reason, so typed PO details are never lost to a refused save.
 *
 * `notice` is something the user must still see about the save that got here
 * (the editor passes "these files did not upload" for a new record saved as
 * Won), shown above the fields.
 *
 * r49: the Global Project Number can be added here too. Optional, like every
 * field on this form - it is often issued after the PO.
 *
 * r50: only the fields that differ from what the record already has are
 * sent, so a PO form left open never writes back a division or billing type
 * someone else changed in the meantime.
 */
import { useState } from 'react';
import { useDataScope } from '../../context/DataScope';
import { Modal } from '../ui/Modal';
import { sameValue } from './editOpportunityForm';
import { localDay } from '../../utils/localDay';

// the app's own vocabularies - see EditOpportunityTabs / OpportunitiesView
const BILLING = ['', 'Fixed FEE', 'T&L', 'T&M', 'Cost Plus'];
const DIVISIONS = ['', 'Services', 'Solutions', 'Software', 'Equipment', 'Parts', 'Process', 'Project', 'Software/Process'];

const label = { fontSize: 10.5, fontWeight: 700, color: '#666', marginBottom: 4, display: 'block' };
const input = {
  width: '100%',
  padding: '8px 11px',
  borderRadius: 6,
  border: '1px solid #DDD',
  fontSize: 12,
  fontFamily: 'Montserrat',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fff',
  color: '#0D0D0D',
};

// today on the user's own calendar (the UTC date is tomorrow after about 8pm Eastern)
const localToday = () => localDay();

export default function WonProjectSetup({ opp, onSave, onClose, notice = '' }) {
  const scope = useDataScope();
  const quoted = Number(opp.quotedPrice ?? opp.quoted_price);
  // what the record has now: the prefills below (the quote as the PO value,
  // today as the PO date) are not on it yet, so they still count as changes
  const stored = {
    poNumber: opp.poNumber ?? opp.po_number,
    poValue: opp.poValue ?? opp.po_value,
    poDateIssued: opp.poDateIssued ?? opp.po_date_issued,
    billingType: opp.billingType ?? opp.billing_type,
    division: opp.division,
    globalProjectNumber: opp.globalProjectNumber ?? opp.global_project_number,
  };
  const [f, setF] = useState({
    poNumber: opp.poNumber || opp.po_number || '',
    // the PO usually equals the quote; with no quoted price leave it blank, never a made-up 0
    poValue: opp.poValue ?? opp.po_value ?? (quoted > 0 ? quoted : ''),
    poDateIssued: opp.poDateIssued || opp.po_date_issued || localToday(),
    billingType: opp.billingType || opp.billing_type || '',
    division: opp.division || '',
    globalProjectNumber: opp.globalProjectNumber || opp.global_project_number || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => {
    setError('');
    setF((p) => ({ ...p, [k]: e.target.value }));
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // only send what was actually filled in - a blank must not wipe a value
      const filled = {};
      if (String(f.poNumber).trim()) filled.poNumber = String(f.poNumber).trim();
      const value = String(f.poValue).trim().replace(/,/g, '');
      if (value !== '') {
        if (!/^\d+(\.\d{1,2})?$/.test(value)) {
          setError('PO value must be a number, like 125000.00.');
          return;
        }
        filled.poValue = Number(value);
      }
      if (f.poDateIssued) filled.poDateIssued = f.poDateIssued;
      if (f.billingType) filled.billingType = f.billingType;
      if (f.division) filled.division = f.division;
      if (String(f.globalProjectNumber).trim()) filled.globalProjectNumber = String(f.globalProjectNumber).trim();
      // ...and only what differs from the record (r50)
      const patch = Object.fromEntries(Object.entries(filled).filter(([k, v]) => !sameValue(v, stored[k])));
      // nothing filled in is the same as "Fill in later", not a failed save
      if (Object.keys(patch).length && (await onSave(patch)) === false) {
        setError('Could not save the project details - try again, or press Fill in later and add them on the project.');
        return;
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const title = `${opp.quoteLetter || opp.quote_letter ? `Q${opp.quoteLetter || opp.quote_letter} · ` : ''}${opp.description || opp.name || 'this opportunity'}`;

  return (
    <Modal title="Won - add the project details" onClose={onClose} width={560}>
      <div style={{ fontFamily: 'Montserrat' }}>
        <div style={{ background: '#E8F5E9', border: '1px solid #C8E6C9', borderRadius: 8, padding: '10px 13px', marginBottom: 16 }}>
          <div style={{ fontSize: 11.5, color: '#1B5E20', fontWeight: 700 }}>✓ Marked Won - already saved</div>
          <div style={{ fontSize: 11, color: '#416344', marginTop: 3, lineHeight: 1.45 }}>{title}</div>
        </div>

        {notice && (
          <div
            role="status"
            style={{
              background: '#FFF8E1',
              border: '1px solid #FDE68A',
              color: '#92400E',
              borderRadius: 6,
              padding: '7px 10px',
              fontSize: 11.5,
              fontWeight: 600,
              marginBottom: 14,
              lineHeight: 1.45,
            }}
          >
            {notice}
          </div>
        )}

        <p style={{ fontSize: 11.5, color: '#555', margin: '0 0 14px', lineHeight: 1.5 }}>
          Fill these in now and the job is ready on {scope ? 'KSA Projects' : 'the Projects tab'}. Anything you skip can be added later from
          the project itself.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={label}>PO Number</label>
            <input style={input} value={f.poNumber} onChange={set('poNumber')} placeholder="e.g. 100195510" autoFocus />
          </div>
          <div>
            <label style={label}>PO Value</label>
            <input style={input} type="number" step="0.01" value={f.poValue} onChange={set('poValue')} placeholder="0.00" />
          </div>
          <div>
            <label style={label}>PO Date Issued</label>
            <input style={input} type="date" value={f.poDateIssued} onChange={set('poDateIssued')} />
          </div>
          <div>
            <label style={label}>Billing Type</label>
            <select style={{ ...input, cursor: 'pointer' }} value={f.billingType} onChange={set('billingType')}>
              {BILLING.map((b) => (
                <option key={b} value={b}>
                  {b || '- select -'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={label}>Division</label>
            <select style={{ ...input, cursor: 'pointer' }} value={f.division} onChange={set('division')}>
              {DIVISIONS.map((d) => (
                <option key={d} value={d}>
                  {d || '- select -'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={label} htmlFor="won-gpn">
              Global Project Number <span style={{ fontWeight: 500, color: '#999' }}>(optional)</span>
            </label>
            <input
              id="won-gpn"
              style={input}
              value={f.globalProjectNumber}
              onChange={set('globalProjectNumber')}
              placeholder="e.g. P26041-010"
            />
          </div>
        </div>

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 14,
              background: '#FFEBEE',
              border: '1px solid #FFCDD2',
              color: '#B71C1C',
              borderRadius: 6,
              padding: '7px 10px',
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, marginTop: 20 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '9px 16px',
              borderRadius: 6,
              border: '1px solid #DDD',
              background: '#fff',
              color: '#666',
              fontSize: 11.5,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'Montserrat',
            }}
          >
            Fill in later
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            style={{
              padding: '9px 20px',
              borderRadius: 6,
              border: 'none',
              background: busy ? '#C8D6C9' : '#1B5E20',
              color: '#fff',
              fontSize: 11.5,
              fontWeight: 700,
              cursor: busy ? 'not-allowed' : 'pointer',
              fontFamily: 'Montserrat',
            }}
          >
            {busy ? 'Saving…' : 'Save project details'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
